import React, { useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Box, Grid, Chip, Button, Divider, Typography } from "@mui/material";
import {
  AttachMoney,
  Person,
  DateRangeOutlined,
  InfoOutlined,
  CheckCircle,
  Cancel,
  LocationOnOutlined,
} from "@mui/icons-material";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import InformationTile from "components/Shared/InformationTile";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";
import { useGetShipmentPriceQuery } from "store/services/shipmentApi";
import { localizeDateTimeShort } from "utils/dateFormats";
import { BaseTabs } from "components/Shared/BaseTabs";
import PriceChangeLogList from "./PriceChangeLogList";

const statusStyles = {
  Paid: { backgroundColor: "#EBF5FB", textColor: "green" },
  Applied: { backgroundColor: "#EBF5FB", textColor: "green" },
  "Not Paid": { backgroundColor: "#FADBD8", textColor: "#C0392B" },
  "Not Applied": { backgroundColor: "#FADBD8", textColor: "#C0392B" },
  Collected: { backgroundColor: "#EBF5FB", textColor: "green" },
  default: { backgroundColor: "#E9ECEF", textColor: "#6C757D" },
};

const remitStatusStyles = {
  Transferred: { backgroundColor: "#EBF5FB", textColor: "green" },
  "Not Transferred": { backgroundColor: "#FADBD8", textColor: "#C0392B" },
  default: { backgroundColor: "#E9ECEF", textColor: "#6C757D" },
};

const getStatusStyle = (status) => statusStyles[status] || statusStyles.default;
const getRemitStatusStyle = (status) => remitStatusStyles[status] || remitStatusStyles.default;

const ShipmentPriceDetail = () => {
  const { priceId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: price, isLoading, error, refetch } = useGetShipmentPriceQuery(priceId, {
    refetchOnMountOrArgChange: true,
  });
  const activeTab = searchParams.get("tab") || "details";

  const breadcrumbs = [
    { title: "Shipment Prices", path: `/app/crm/shipment/${price?.shipment?._id}/detail?tab=pricing` },
    { title: "Price Detail" },
  ];

  const tabs = [
    { label: "Details", value: "details" },
    { label: "History", value: "history" },
  ];

  const handleTabChange = (newValue) => {
    setSearchParams({ tab: newValue });
  };

  useEffect(() => {
    refetch();
  }, [priceId, refetch]);

  const renderContent = (tab) => {
    if (!priceId) {
      return (
        <Box sx={{ p: 3 }}>
          <Typography color="error">Invalid or missing price ID</Typography>
        </Box>
      );
    }
    switch (tab) {
      case "details":
        return (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={4}>
              <Grid item xs={3}>
                <InformationTile
                  icon={() => <InfoOutlined color="primary" />}
                  title="Fee Item"
                  subtitle={price?.feeItem?.name || "N/A"}
                />
              </Grid>
              <Grid item xs={3}>
                <InformationTile
                  icon={() => <InfoOutlined color="primary" />}
                  title="Charge Type"
                  subtitle={price?.chargeType || "N/A"}
                />
              </Grid>
              {price?.pricePurpose && (
                <Grid item xs={3}>
                  <InformationTile
                    icon={() => <InfoOutlined color="error" />}
                    title="Price Purpose"
                    subtitle={price.pricePurpose}
                  />
                </Grid>
              )}

              <Grid item xs={3}>
                <InformationTile
                  icon={() => <AttachMoney color="primary" />}
                  title="Amount"
                  subtitle={
                    price?.charge !== undefined
                      ? `${price.chargeType === "Discount" ? "-" : ""}Rs.${Math.abs(price.charge).toFixed(2)}`
                      : "N/A"
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <InformationTile
                  icon={() => <Person color="primary" />}
                  title="Payer"
                  subtitle={price?.payer || "N/A"}
                />
              </Grid>
              <Grid item xs={3}>
                <InformationTile
                  icon={() => <LocationOnOutlined color="primary" />}
                  title="Payment Location"
                  subtitle={price?.paymentLocation || "N/A"}
                />
              </Grid>
              <Grid item xs={3}>
                <InformationTile
                  icon={() => <InfoOutlined color="primary" />}
                  title="Status"
                  subtitle={
                    <Box display="flex" flexDirection="column" alignItems="start" mt={1}>
                      <Chip
                        label={
                          price?.chargeType === "Charge" || price?.chargeType === "Cash on Delivery"
                            ? price?.paymentStatus
                            : price?.discountStatus || "N/A"
                        }
                        style={{
                          backgroundColor: getStatusStyle(
                            price?.chargeType === "Charge" || price?.chargeType === "Cash on Delivery"
                              ? price?.paymentStatus
                              : price?.discountStatus
                          ).backgroundColor,
                          color: getStatusStyle(
                            price?.chargeType === "Charge" || price?.chargeType === "Cash on Delivery"
                              ? price?.paymentStatus
                              : price?.discountStatus
                          ).textColor,
                          fontWeight: "bold",
                          textTransform: "uppercase",
                          fontSize: "0.7rem",
                          height: "20px",
                          padding: "0 6px",
                          borderRadius: "12px",
                          marginTop: "4px",
                          width: "auto",
                        }}
                      />
                    </Box>
                  }
                />
              </Grid>
              {price?.chargeType === "Cash on Delivery" && (
                <>
                  <Grid item xs={3}>
                    <InformationTile
                      icon={() => price.remitStatus === "Transferred" ? <CheckCircle color="primary" /> : <Cancel color="primary" />}
                      title="COD Remitted"
                      subtitle={
                        <Box display="flex" flexDirection="column" alignItems="start" mt={1}>
                          <Chip
                            label={price?.remitStatus || "N/A"}
                            style={{
                              backgroundColor: getRemitStatusStyle(price?.remitStatus).backgroundColor,
                              color: getRemitStatusStyle(price?.remitStatus).textColor,
                              fontWeight: "bold",
                              textTransform: "uppercase",
                              fontSize: "0.7rem",
                              height: "20px",
                              padding: "0 6px",
                              borderRadius: "12px",
                              marginTop: "4px",
                              width: "auto",
                            }}
                          />
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <InformationTile
                      icon={() => <InfoOutlined color="primary" />}
                      title="Remit Source"
                      subtitle={
                        price?.remitSource && price?.remitSourceRef ? (
                          <Link
                            to={`/app/crm/cod-transfer/${price.remitSourceRef}/detail`}
                            className="cursor-pointer text-blue-600 hover:underline"
                          >
                            {price.remitSource}
                          </Link>
                        ) : (
                          "N/A"
                        )
                      }
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={3}>
                <InformationTile
                  icon={() => <InfoOutlined color="primary" />}
                  title="Source"
                  subtitle={
                    price?.source && price?.sourceRef ? (
                      <Link
                        to={`/app/crm/${
                          price.source.startsWith("COC-") ? "coc-collection" :
                          price.source.startsWith("COD-") ? "cod-transfer" :
                          price.source.startsWith("DC-") ? "delivery-collection" :
                          price.source.startsWith("PP-") ? "prepayment" : ""
                        }/${price.sourceRef}/detail`}
                        className="cursor-pointer text-blue-600 hover:underline"
                      >
                        {price.source}
                      </Link>
                    ) : (
                      "N/A"
                    )
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <InformationTile
                  icon={() => <Person color="primary" />}
                  title="Created User"
                  subtitle={price?.user?.name || "N/A"}
                />
              </Grid>
              <Grid item xs={3}>
                <InformationTile
                  icon={() => <Person color="primary" />}
                  title="Updated By"
                  subtitle={price?.updatedBy?.name || "N/A"}
                />
              </Grid>
              <Grid item xs={3}>
                <InformationTile
                  icon={() => <Person color="primary" />}
                  title="Responsible User"
                  subtitle={price?.responsibleUser?.name || "N/A"}
                />
              </Grid>
              <Grid item xs={3}>
                <InformationTile
                  icon={() => <DateRangeOutlined color="primary" />}
                  title="Created At"
                  subtitle={
                    price?.createdAt
                      ? localizeDateTimeShort(price.createdAt)
                      : "N/A"
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <InformationTile
                  icon={() => <DateRangeOutlined color="primary" />}
                  title="Updated At"
                  subtitle={
                    price?.updatedAt
                      ? localizeDateTimeShort(price.updatedAt)
                      : "N/A"
                  }
                />
              </Grid>
            </Grid>
          </Box>
        );
      case "history":
        return <PriceChangeLogList priceId={priceId} />;
      default:
        return null;
    }
  };

  return (
    <BreadcrumbLayout
      breadcrumbs={breadcrumbs}
      isBusy={isLoading}
    >
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <BaseTabs
          tabs={tabs}
          value={activeTab}
          onChange={handleTabChange}
        >
          {renderContent(activeTab)}
        </BaseTabs>
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default ShipmentPriceDetail;